import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { InputLabel, FormControl, Select, MenuItem, Box, Typography } from "@mui/material";
import {
  fetchRevenueDropdown,
  fetchFilteredRevenueShare,
} from "../../slices/revenue.slice";
import { RootState } from "../../store";
import { RevenueSpreadsheetComponent } from "./RevenueSpreadsheetComponent";
import { MultiSelectDropdown } from "../../components/MultiSelect/MultiSelectDropdownComponent";
import CustomButton from "../../components/Button";

const RevenueManagement: React.FC = () => {
  const [selectedCropTypes, setSelectedCropTypes] = useState<any[]>([]);
  const [selectedLandlords, setSelectedLandlords] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(2025);
  const [initialLoad, setInitialLoad] = useState(false);

  const dispatch = useAppDispatch();

  const {
    revenueShares,
    cropType,
    landlord,
    calendarYears,
    loading,
  } = useAppSelector((state: RootState) => state.revenue);

  useEffect(() => {
    dispatch(fetchRevenueDropdown("calendarYear"));
    dispatch(fetchRevenueDropdown("landlord"));
    dispatch(fetchRevenueDropdown("cropType"));
  }, [dispatch]);

  useEffect(() => {
    if (cropType.length > 0) {
      setSelectedCropTypes(cropType);
    }
    if (landlord.length > 0) {
      setSelectedLandlords(landlord);
    }
    if (calendarYears.length > 0 && !calendarYears.find((year) => year.id === selectedYear)) {
      setSelectedYear(calendarYears[0]?.id || 2025);
    }
  }, [cropType, landlord, calendarYears]);

  useEffect(() => {
    if (
      !initialLoad &&
      cropType.length > 0 &&
      landlord.length > 0 &&
      calendarYears.length > 0
    ) {
      dispatch(
        fetchFilteredRevenueShare({
          year: selectedYear,
          cropTypeIds: cropType.map((x) => x.id),
          landlordIds: landlord.map((x: { id: number }) => x.id),
        })
      );
      setInitialLoad(true);
    }
  }, [cropType, landlord, calendarYears, selectedYear, initialLoad, dispatch]);

  const handleSearch = () => {
    const cropTypeIdsArray = selectedCropTypes.map((value) => value.id);
    const landlordIdsArray = selectedLandlords.map((value) => value.id);

    const requestData = {
      cropTypeIds: cropTypeIdsArray,
      landlordIds: landlordIdsArray,
      year: selectedYear,
    };

    dispatch(fetchFilteredRevenueShare(requestData));
  };

  const handleYearChange = (event: any) => {
    const newYear = parseInt(event.target.value);
    setSelectedYear(newYear);

    const cropTypeIdsArray = selectedCropTypes.map((value) => value.id);
    const landlordIdsArray = selectedLandlords.map((value) => value.id);

    const requestData = {
      cropTypeIds: cropTypeIdsArray,
      landlordIds: landlordIdsArray,
      year: newYear,
    };

    dispatch(fetchFilteredRevenueShare(requestData));
  };

  const handleSaveSuccess = () => {
    const requestData = {
      cropTypeIds: selectedCropTypes.map((value) => value.id),
      landlordIds: selectedLandlords.map((value) => value.id),
      year: selectedYear,
    };

    dispatch(fetchFilteredRevenueShare(requestData));
  };

  return (
    <Box display={"flex"} p={3} gap={2} flexDirection={"column"}>
      <Box display={"flex"} gap={2} alignItems={"center"}>
        <FormControl>
          <InputLabel>Year</InputLabel>
          <Select
            labelId=""
            value={selectedYear}
            label="Year"
            onChange={handleYearChange}
            size="small"
          >
            {calendarYears.map((year) => (
              <MenuItem key={year.id} value={year.id}>
                {year.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <MultiSelectDropdown
          options={cropType}
          label="Crop Type"
          selected={selectedCropTypes}
          setSelected={setSelectedCropTypes}
        />

        <MultiSelectDropdown
          options={landlord}
          label="Landlord"
          selected={selectedLandlords}
          setSelected={setSelectedLandlords}
        />

        <CustomButton
          onClick={handleSearch}
          sx={{ width: "110px" }}
          disabled={
            !selectedCropTypes.length ||
            !selectedLandlords.length
          }
        >
          Search
        </CustomButton>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography variant="h6" color="textSecondary">
            Loading data...
          </Typography>
        </Box>
      ) : (
        <RevenueSpreadsheetComponent
          data={revenueShares}
          cropOptions={selectedCropTypes}
          landlordOptions={selectedLandlords}
          selectedYear={selectedYear}
          onSaveSuccess={handleSaveSuccess}
        />
      )}
    </Box>
  );
};

export default RevenueManagement;